import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'

import { routeToPath, type TCommunityJoinParams } from '@/src/general/constants/routes'
import { useRoutes } from '@/src/general/utils/routes'
import { stopPropagation } from '@/src/general/utils/stopPropagation'

import type { TCommunityApiHasJoinedInfos, TMediumCommunityApi } from '../../../api/communityApi.types'
import { useCommunityActiveSubscription } from '../../../services/useCommunityActiveSubscription'
import { useCommunitySubscriptionRoute } from '../../hooks/useCommunitySubscriptionRoute'
import { CommunityShareModal } from '../CommunityShareModal'
import type { TCommunityCardVariant } from './CommunityCard.types'
import { CommunityCardBase } from './CommunityCardBase'
import { CommunityCardBaseV2 } from './CommunityCardBaseV2'

export type TCommunityCardProps = Omit<TCommunityJoinParams, 'course'> & {
  className?: string
  community: TMediumCommunityApi & TCommunityApiHasJoinedInfos
  isLoading?: boolean
  showSubscribeButton?: boolean
  fullWidth?: boolean
  testId?: string
  onCustomCardPress?: VoidFunction
  variant?: TCommunityCardVariant
}

export const CommunityCard = ({
  className,
  community,
  source,
  origin,
  isLoading: isCommunityLoading,
  showSubscribeButton,
  fullWidth,
  testId,
  onCustomCardPress,
  variant,
}: TCommunityCardProps): ReactNode => {
  const { routes } = useRoutes()
  const router = useRouter()
  const subscription = useCommunityActiveSubscription(community.id)
  const subscriptionRoute = useCommunitySubscriptionRoute({
    ...community,
    source,
    origin,
    course: 'direct',
  })
  const CardComponent = variant === 'prayerSpaceV2' ? CommunityCardBaseV2 : CommunityCardBase

  return (
    <CommunityShareModal {...community} trackingSource={source}>
      {({ isLoading: isShareLoading, onShare }) => (
        <CardComponent
          className={className}
          community={community}
          subscription={subscription}
          showSubscribeButton={showSubscribeButton}
          testId={testId}
          onCardPress={onCustomCardPress ? stopPropagation(onCustomCardPress) : undefined}
          href={routeToPath(routes.community({ id: community.id, slug: community.slug, origin }))}
          fullWidth={fullWidth}
          onSharePress={stopPropagation(onShare)}
          onSubscribePress={stopPropagation(() => router.navigate(subscriptionRoute))}
          isLoading={isCommunityLoading || isShareLoading}
        />
      )}
    </CommunityShareModal>
  )
}
